import React, { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PackageCheck } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../../store";
import { fetchPendingAssignment } from "../../../store/slices/orderSlice";
import {
  fetchWarehouseNamesAndIdsThunk,
  updateOrderProductMaterialThunk,
} from "../../../store/slices/warehouseSlice";
import PaginationControls from "../../../utils/PaginationControls";
import WarehouseAssignModal from "./WarehouseAssignModal";
import { formatToDDMMYYYY } from "../../../utils/formatDateUtils";
import { Edit3 } from "lucide-react";
import BomSelectionModal from "../common/UpdateBomModal";
import toast from "react-hot-toast";

interface WarehouseAssignTabProps {
  searchTerm?: string;
  dateRange?: { from: string; to: string };
}

const WarehouseAssignTab: React.FC<WarehouseAssignTabProps> = ({
  searchTerm,
  dateRange = { from: "", to: "" },
}) => {
  const dispatch: RootDispatch = useDispatch();
  const { pendingAssignment, paginations, loading } = useSelector(
    (state: RootState) => state.order,
  );
  const { warehouseNamesAndIds } = useSelector(
    (state: RootState) => state.warehouse,
  );

  const [page, setPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isChangeBomModalOpen, setIsChangeBomModalOpen] = useState(false);

  const [visibleProductsMap, setVisibleProductsMap] = useState<
    Record<string, number>
  >({});

  const handleBomSelect = async ({
    selectedBomId,
    oldBomId,
  }: {
    selectedBomId: string;
    oldBomId?: string | null;
  }) => {
    try {
      const res = await dispatch(
        updateOrderProductMaterialThunk({
          orderId: selectedOrder?._id,
          bomId: selectedBomId,
          oldBomId: oldBomId,
        }),
      ).unwrap();
      if (res.success) {
        await fetchPendingAssignmentsCallback();
      }
      setIsChangeBomModalOpen(false);
    } catch (error) {
      toast.error("Failed to update BOM" + error);
    }
  };

  const fetchPendingAssignmentsCallback = useCallback(async () => {
    await dispatch(
      fetchPendingAssignment({
        page,
        search: searchTerm,
        startDate: dateRange.from,
        endDate: dateRange.to,
      }),
    );
  }, [dateRange.from, dateRange.to, dispatch, page, searchTerm]);

  useEffect(() => {
    fetchPendingAssignmentsCallback();
  }, [fetchPendingAssignmentsCallback]);

  useEffect(() => {
    dispatch(fetchWarehouseNamesAndIdsThunk());
  }, [dispatch]);

  const warehouseOptions = warehouseNamesAndIds.map((w) => ({
    value: w._id,
    label: w.name,
  }));

  const numCols = 5;
  const columns: any[][] = Array.from({ length: numCols }, () => []);
  pendingAssignment.forEach((order, index) => {
    columns[index % numCols].push(order);
  });

  return (
    <>
      <section>
        <h2 className="text-lg font-semibold text-slate-800 mb-6 flex items-center">
          <span className="w-1 h-6 bg-orange-500 mr-3 rounded-full" />
          Pending Warehouse Assignment
          <span className="ml-2 bg-orange-100 text-orange-800 text-sm px-2.5 py-0.5 rounded-full">
            {paginations.pendingAssignment?.total || 0}
          </span>
        </h2>

        {pendingAssignment.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            All orders assigned
          </div>
        ) : (
          <div className="flex gap-5 items-start">
            {columns.map((colItems, colIdx) => (
              <div key={colIdx} className="flex-1 flex flex-col gap-5">
                {colItems.map((order) => {
                  const limit = visibleProductsMap[order._id] || 5;
                  const items = order.goods || [];

                  return (
                    <motion.div
                      key={order._id}
                      layout
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      whileHover={{ y: -4 }}
                      onClick={() => {
                        setSelectedOrder(order);
                        setIsModalOpen(true);
                      }}
                      className="bg-white rounded-2xl p-6 shadow-xs border border-gray-100 hover:shadow-md transition-shadow cursor-pointer relative overflow-hidden flex flex-col min-h-[380px]"
                    >
                      <div className="flex items-start mb-4 h-[60px]">
                        <div className="bg-orange-50 p-2 rounded-lg">
                          <PackageCheck size={18} className="text-orange-600" />
                        </div>
                        <div className="ml-3 flex-1 overflow-hidden">
                          <div className="flex items-center justify-between gap-2">
                            <h3 className="font-medium text-sm text-slate-900 truncate">
                              Order #{order.orderNumber}
                            </h3>

                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedOrder(order);
                                setIsChangeBomModalOpen(true);
                              }}
                              className="flex shrink-0 items-center gap-2 text-sm font-bold text-slate-800 hover:text-blue-600"
                            >
                              <Edit3 size={14} className="text-slate-400" />
                            </button>
                          </div>
                          <p className="text-xs text-gray-600 truncate max-w-32">
                            {order.customerId?.name ||
                              order.whatsappCustomerId?.productRegistration
                                ?.name ||
                              order.whatsappCustomerId?.customerName ||
                              "Unknown Customer"}
                          </p>
                          <p className="text-[10px] leading-none text-gray-500 truncate max-w-32">
                            {formatToDDMMYYYY(order?.createdDate) ||
                              "Unknown Date"}
                          </p>
                        </div>
                      </div>

                      <div className="mb-4">
                        <motion.div
                          layout
                          className="space-y-1.5 overflow-hidden"
                        >
                          {items.slice(0, 5).map((item: any, idx: number) => (
                            <div
                              key={`${order._id}-${idx}`}
                              className="flex justify-between items-center p-2 bg-gray-50 rounded-lg text-[11px] h-[30px]"
                            >
                              <span className="font-medium text-gray-800 truncate pr-2">
                                {item.product?.productName ||
                                  item.rawMaterial?.name ||
                                  "Unknown"}
                              </span>
                              {item.isExtra === true && (
                                <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-600 text-[6px] font-black uppercase shrink-0">
                                  Extra
                                </span>
                              )}
                              <span className="text-orange-600 font-bold shrink-0">
                                × {item.quantity} {item.unit || ""}
                              </span>
                            </div>
                          ))}

                          <AnimatePresence initial={false}>
                            {limit > 5 && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{
                                  duration: 0.3,
                                  ease: "easeInOut",
                                }}
                                className="space-y-1.5 overflow-hidden mt-1.5"
                              >
                                {items
                                  .slice(5, limit)
                                  .map((item: any, idx: number) => (
                                    <div
                                      key={`${order._id}-${idx + 5}`}
                                      className="flex justify-between items-center p-2 bg-gray-50 rounded-lg text-[11px] h-[30px]"
                                    >
                                      <span className="font-medium text-gray-800 truncate pr-2">
                                        {item.product?.productName ||
                                          item.rawMaterial?.name ||
                                          "Unknown"}
                                      </span>
                                      {item.isExtra === true && (
                                        <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-600 text-[6px] font-black uppercase shrink-0">
                                          Extra
                                        </span>
                                      )}
                                      <span className="text-orange-600 font-bold shrink-0">
                                        × {item.quantity} {item.unit || ""}
                                      </span>
                                    </div>
                                  ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>

                        {items.length > limit && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setVisibleProductsMap((prev) => ({
                                ...prev,
                                [order._id]: limit + 5,
                              }));
                            }}
                            className="w-full py-1.5 text-[10px] text-orange-600 font-bold bg-orange-50 hover:bg-orange-100 rounded-md transition-colors mt-2"
                          >
                            Show {Math.min(5, items.length - limit)} more
                          </button>
                        )}
                      </div>

                      <div className="mt-auto pt-3 border-t border-gray-100 text-xs text-orange-600 font-bold text-center uppercase tracking-wider">
                        Click to assign
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
        {paginations.pendingAssignment &&
          paginations.pendingAssignment.total > 0 && (
            <div className="mt-8 border-t border-gray-100 pt-6">
              <PaginationControls
                currentPage={page}
                totalPages={paginations.pendingAssignment.pages}
                onPageChange={setPage}
                isLoading={loading}
                totalItems={paginations.pendingAssignment.total}
              />
            </div>
          )}
      </section>

      <WarehouseAssignModal
        key={selectedOrder?._id + (selectedOrder?.updatedAt || "")}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        fetchPendingRequests={async () =>
          await fetchPendingAssignmentsCallback()
        }
        order={selectedOrder}
        warehouseOptions={warehouseOptions}
      />
      <BomSelectionModal
        open={isChangeBomModalOpen}
        oldBomId={selectedOrder?.bomId}
        onClose={() => {
          setIsChangeBomModalOpen(false);
          setSelectedOrder(null);
        }}
        onSelect={handleBomSelect}
      />
    </>
  );
};

export default WarehouseAssignTab;
